import { eventCategories, events } from '@js/utils/event';
import { getOffset, isMobileView, logEvent, qs, qsa } from '@js/utils/toolbox';
import { onMounted } from 'vue';

// Constants controlling scroll behavior
const SCROLL_OFFSET = 40;
const MIN_SCROLL_THRESHOLD = 5;
const MIN_TOP_POSITION = 30;

// Zoom-safe epsilon (accounts for fractional pixels under zoom)
const getEps = () => Math.max(2, (window.devicePixelRatio || 1) * 2);

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

// Track whether scroll listeners have already been added
let isEventListenerAdded = false;

// Cached DOM references (rebuilt on mount to ensure accuracy)
let scrollUpBtn = qs('.filtersScrollBtn--up');
let scrollDownBtn = qs('.filtersScrollBtn--down');
let content = qs('.filtersContent');
let parent = qs('#filters');
let views = qsa('.filtersView');

/**
 * Returns each view's top position in scroll coordinate space
 * (same coordinate system as content.scrollTop)
 */
const getViewTopsInScrollSpace = () => {
	if (!content || !views?.length) {
		return [];
	}

	const contentRect = content.getBoundingClientRect();
	const baseScrollTop = content.scrollTop;

	return views.map((view) => {
		const rect = view.getBoundingClientRect();
		return (rect.top - contentRect.top) + baseScrollTop;
	});
};

/**
 * Stable active view index (zoom-safe).
 * Active view = last view whose top is above current scroll + offset.
 */
const getActiveViewIndex = () => {
	if (!content || !views?.length) {
		return 0;
	}

	const eps = getEps();
	const tops = getViewTopsInScrollSpace();
	const cursor = content.scrollTop + SCROLL_OFFSET + eps;

	let idx = 0;
	for (let i = 0; i < tops.length; i++) {
		if (tops[i] <= cursor) {
			idx = i;
		} else {
			break;
		}
	}
	return idx;
};

/**
 * Shows or hides a scroll button by toggling a visibility class
 */
const toggleButtonVisibility = (button, shouldShow) => {
	if (!button) {
		return;
	}
	button.classList.toggle('filtersScrollBtn--visible', shouldShow);
};

export function useFiltersScroll ({ direction }) {
	const handleButtonClick = () => {
		if (!content || !views?.length) {
			return;
		}

		// Cancel in-flight smooth scroll so measurements are stable
		content.scrollTo({ top: content.scrollTop, behavior: 'auto' });

		const currentIndex = getActiveViewIndex();

		let targetIndex = currentIndex + (direction === 'up' ? -1 : 1);
		targetIndex = clamp(targetIndex, 0, views.length - 1);

		const tops = getViewTopsInScrollSpace();
		const eps = getEps();

		let yPos = tops[targetIndex] - SCROLL_OFFSET;

		if (direction === 'up' && yPos < MIN_TOP_POSITION + eps) {
			yPos = 0;
		}

		yPos = Math.max(0, yPos);

		/**
		 * Edge case: if scrolling to last view would overflow the container,
		 * scroll the whole page instead so that the filter block remains in view
		 */
		const lastViewProximity = yPos + content.clientHeight - SCROLL_OFFSET;
		if (
			lastViewProximity > content.scrollHeight
			&& parent?.getBoundingClientRect().top > 0
		) {
			window.scrollTo({
				top: getOffset(parent).top,
				behavior: 'smooth'
			});
		}

		// Smooth-scroll the filters container
		content.scrollTo({
			top: yPos,
			behavior: 'smooth'
		});
	};

	/**
	 * Updates visibility of scroll buttons depending on scroll position
	 * Also logs an event the first time the list is scrolled
	 */
	const updateButtonVisibility = () => {
		if (!content) {
			return;
		}

		const eps = getEps();
		const scrollTop = content.scrollTop;

		// Show "Up" button only when scrolled down enough
		toggleButtonVisibility(
			scrollUpBtn,
			scrollTop >= MIN_SCROLL_THRESHOLD - eps
		);

		// Show "Down" button when not at bottom (zoom-safe comparison)
		toggleButtonVisibility(
			scrollDownBtn,
			scrollTop + content.clientHeight < content.scrollHeight - eps
		);

		// Log scroll event only once
		const filters = qs('#filters');
		if (
			scrollTop >= MIN_SCROLL_THRESHOLD
			&& filters
			&& !filters.classList.contains('scrolled')
		) {
			filters.classList.add('scrolled');
			logEvent(events.scrollFilterList, eventCategories.scroll);
		}
	};

	/**
	 * Setup scroll listeners on mount
	 */
	onMounted(() => {
		if (isEventListenerAdded || isMobileView()) {
			return;
		}

		// Re-query DOM (in case of re-render)
		views = qsa('.filtersView');
		scrollUpBtn = qs('.filtersScrollBtn--up');
		scrollDownBtn = qs('.filtersScrollBtn--down');
		content = qs('.filtersContent');
		parent = qs('#filters');

		content?.addEventListener('scroll', updateButtonVisibility, {
			passive: true
		});
		window.addEventListener('scroll', updateButtonVisibility, {
			passive: true
		});

		isEventListenerAdded = true;

		updateButtonVisibility();
	});

	return { handleButtonClick };
}
